import type { ApiResponse } from "../../../shared/types/api";
import { httpClient } from "../http-client";
import { NEW_API_ENDPOINTS } from "../../../shared/constants/api";
import type {
  TodoTemplate,
  CreateTodoTemplateRequest,
  UpdateTodoTemplateRequest,
  ReorderTodoTemplatesRequest,
  OrderTodo,
  UpdateTodoStatusRequest,
  UpdateTodoAssigneesRequest,
  AddTodoNoteRequest,
  TodoNote,
  CompanyTodosFilter,
} from "../../../domain/types/todo";

// ========== Todo Template API Service ==========

export class TodoTemplateApiService {
  async getTemplates(
    companyId: number,
    productId: number
  ): Promise<ApiResponse<TodoTemplate[]>> {
    const response = await httpClient.get<ApiResponse<TodoTemplate[]>>(
      NEW_API_ENDPOINTS.todoTemplateController.GET.list(companyId, productId)
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "템플릿 목록을 가져오지 못했습니다.");
    }

    return response.data;
  }

  async getTemplate(
    companyId: number,
    productId: number,
    templateId: number
  ): Promise<ApiResponse<TodoTemplate>> {
    const response = await httpClient.get<ApiResponse<TodoTemplate>>(
      NEW_API_ENDPOINTS.todoTemplateController.GET.detail(companyId, productId, templateId)
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "템플릿 상세 정보를 가져오지 못했습니다.");
    }

    return response.data;
  }

  async createTemplate(
    companyId: number,
    productId: number,
    request: CreateTodoTemplateRequest
  ): Promise<ApiResponse<TodoTemplate>> {
    const response = await httpClient.post<ApiResponse<TodoTemplate>>(
      NEW_API_ENDPOINTS.todoTemplateController.POST.create(companyId, productId),
      request
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "템플릿을 생성하지 못했습니다.");
    }

    return response.data;
  }

  async updateTemplate(
    companyId: number,
    productId: number,
    templateId: number,
    request: UpdateTodoTemplateRequest
  ): Promise<ApiResponse<TodoTemplate>> {
    const response = await httpClient.put<ApiResponse<TodoTemplate>>(
      NEW_API_ENDPOINTS.todoTemplateController.PUT.update(companyId, productId, templateId),
      request
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "템플릿을 수정하지 못했습니다.");
    }

    return response.data;
  }

  async reorderTemplates(
    companyId: number,
    productId: number,
    request: ReorderTodoTemplatesRequest
  ): Promise<ApiResponse<TodoTemplate[]>> {
    const response = await httpClient.put<ApiResponse<TodoTemplate[]>>(
      NEW_API_ENDPOINTS.todoTemplateController.PUT.reorder(companyId, productId),
      request
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "템플릿 순서를 변경하지 못했습니다.");
    }

    return response.data;
  }

  async toggleActive(
    companyId: number,
    productId: number,
    templateId: number
  ): Promise<ApiResponse<TodoTemplate>> {
    const response = await httpClient.patch<ApiResponse<TodoTemplate>>(
      NEW_API_ENDPOINTS.todoTemplateController.PATCH.toggleActive(companyId, productId, templateId)
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "템플릿 활성화 상태를 변경하지 못했습니다.");
    }

    return response.data;
  }

  async deleteTemplate(
    companyId: number,
    productId: number,
    templateId: number
  ): Promise<ApiResponse<boolean>> {
    const response = await httpClient.delete<ApiResponse<boolean> | void>(
      NEW_API_ENDPOINTS.todoTemplateController.DELETE.delete(companyId, productId, templateId)
    );

    // 백엔드가 204 No Content를 반환하는 경우 처리
    if (response.status === 204 || !response.data) {
      return { data: true };
    }

    if (response.data?.errorCode) {
      throw new Error(response.data.errorMsg || "템플릿을 삭제하지 못했습니다.");
    }

    return response.data as ApiResponse<boolean>;
  }
}

// ========== Order Todo API Service ==========

export class OrderTodoApiService {
  async getOrderTodos(
    companyId: number,
    orderId: number
  ): Promise<ApiResponse<OrderTodo[]>> {
    const response = await httpClient.get<ApiResponse<OrderTodo[]>>(
      NEW_API_ENDPOINTS.orderTodoController.GET.list(companyId, orderId)
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "주문 To-Do 목록을 가져오지 못했습니다.");
    }

    return response.data;
  }

  async getOrderTodo(
    companyId: number,
    orderId: number,
    todoId: number
  ): Promise<ApiResponse<OrderTodo>> {
    const response = await httpClient.get<ApiResponse<OrderTodo>>(
      NEW_API_ENDPOINTS.orderTodoController.GET.detail(companyId, orderId, todoId)
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "To-Do 상세 정보를 가져오지 못했습니다.");
    }

    return response.data;
  }

  async getCompanyTodos(
    companyId: number,
    filter?: CompanyTodosFilter
  ): Promise<ApiResponse<OrderTodo[]>> {
    const params = new URLSearchParams();
    if (filter?.status) params.append("status", filter.status);
    if (filter?.assigneeUserId) params.append("assigneeUserId", filter.assigneeUserId.toString());
    if (filter?.productId) params.append("productId", filter.productId.toString());
    if (filter?.priority) params.append("priority", filter.priority);
    if (filter?.dueDateFrom) params.append("dueDateFrom", filter.dueDateFrom);
    if (filter?.dueDateTo) params.append("dueDateTo", filter.dueDateTo);

    const query = params.toString();
    const url = `${NEW_API_ENDPOINTS.orderTodoController.GET.companyTodos(companyId)}${query ? `?${query}` : ""}`;

    const response = await httpClient.get<ApiResponse<OrderTodo[]>>(url);

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "회사 To-Do 목록을 가져오지 못했습니다.");
    }

    return response.data;
  }

  async getMyTodos(companyId: number): Promise<ApiResponse<OrderTodo[]>> {
    const response = await httpClient.get<ApiResponse<OrderTodo[]>>(
      NEW_API_ENDPOINTS.orderTodoController.GET.myTodos(companyId)
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "내 To-Do 목록을 가져오지 못했습니다.");
    }

    return response.data;
  }

  async updateStatus(
    companyId: number,
    orderId: number,
    todoId: number,
    request: UpdateTodoStatusRequest
  ): Promise<ApiResponse<OrderTodo>> {
    const response = await httpClient.patch<ApiResponse<OrderTodo>>(
      NEW_API_ENDPOINTS.orderTodoController.PATCH.updateStatus(companyId, orderId, todoId),
      request
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "To-Do 상태를 변경하지 못했습니다.");
    }

    return response.data;
  }

  async updateAssignees(
    companyId: number,
    orderId: number,
    todoId: number,
    request: UpdateTodoAssigneesRequest
  ): Promise<ApiResponse<OrderTodo>> {
    const response = await httpClient.put<ApiResponse<OrderTodo>>(
      NEW_API_ENDPOINTS.orderTodoController.PUT.updateAssignees(companyId, orderId, todoId),
      request
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "담당자를 변경하지 못했습니다.");
    }

    return response.data;
  }

  async addNote(
    companyId: number,
    orderId: number,
    todoId: number,
    request: AddTodoNoteRequest
  ): Promise<ApiResponse<TodoNote>> {
    const response = await httpClient.post<ApiResponse<TodoNote>>(
      NEW_API_ENDPOINTS.orderTodoController.POST.addNote(companyId, orderId, todoId),
      request
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "메모를 추가하지 못했습니다.");
    }

    return response.data;
  }
}

export const todoTemplateApiService = new TodoTemplateApiService();
export const orderTodoApiService = new OrderTodoApiService();
